import React, { useEffect } from "react";
import {
  BrowserRouter as Router, Routes, Route,
  Navigate, useLocation
} from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { AnimatePresence, motion } from "framer-motion";
import ReactGA from "react-ga4";
import Layout    from "./components/Layout";
import Dashboard from "./pages/Dashboard";
import Assessment from "./pages/Assessment";
import Landing    from "./pages/Landing";
import Pricing    from "./pages/Pricing";
import Terms      from "./pages/Terms";
import Privacy    from "./pages/Privacy";
import NotFound   from "./pages/NotFound";
import CookieBanner   from "./components/CookieBanner";
import SessionTimeout from "./components/SessionTimeout";
import { TrialExpiredModal } from "./components/TrialComponents";
import Vendors   from "./pages/Vendors";
import Compliance from "./pages/Compliance";
import Insurance from "./pages/Insurance";
import Reports   from "./pages/Reports";
import Login     from "./pages/Login";
import Settings  from "./pages/Settings";
import Contact   from "./pages/Contact";
import Frameworks from "./pages/Frameworks";
import Verify     from "./pages/Verify";
import Join       from "./pages/Join";

// ── Google Analytics 4 ────────────────────────────────────────────────────────
ReactGA.initialize("G-FEQ9SKBFG3"); // ← Replace with your real Measurement ID

// ─── Query client — disable retries on 401 so server isn't flooded ────────────
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (failureCount, error: any) => {
        if (error?.response?.status === 401) return false;
        return failureCount < 2;
      },
      refetchOnWindowFocus: () => !!localStorage.getItem("token"),
    },
  },
});

// ─── Protected Route guard ────────────────────────────────────────────────────
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const token = localStorage.getItem("token");
  if (!token) {
    return <Navigate to="/login" replace />;
  }
  return <>{children}</>;
}

// ─── Page transition variants ─────────────────────────────────────────────────
const pageVariants = {
  initial: { opacity: 0 },
  animate: { opacity: 1 },
  exit:    { opacity: 0 },
};
const transition = { duration: 0.15 };

// ─── Animated wrapper helper ──────────────────────────────────────────────────
function Page({ children }: { children: React.ReactNode }) {
  return (
    <motion.div
      variants={pageVariants}
      initial="initial"
      animate="animate"
      exit="exit"
      transition={transition}
    >
      {children}
    </motion.div>
  );
}

// ─── Protected page helper ────────────────────────────────────────────────────
function ProtectedPage({ children }: { children: React.ReactNode }) {
  return (
    <ProtectedRoute>
      <Layout>
        <Page>{children}</Page>
      </Layout>
    </ProtectedRoute>
  );
}

// ─── Routes ───────────────────────────────────────────────────────────────────
function AnimatedRoutes() {
  const location = useLocation();

  // Fire GA4 pageview on every route change
  useEffect(() => {
    ReactGA.send({
      hitType: "pageview",
      page:    location.pathname + location.search,
    });
  }, [location]);

  return (
    <AnimatePresence mode="sync">
      <Routes location={location} key={location.pathname}>

        {/* ── PUBLIC PAGES ─────────────────────────────────────────── */}
        <Route path="/" element={
          <Page><Landing /></Page>
        } />
        <Route path="/pricing" element={
          <Page><Pricing /></Page>
        } />
        <Route path="/terms" element={
          <Page><Terms /></Page>
        } />
        <Route path="/privacy" element={
          <Page><Privacy /></Page>
        } />
        <Route path="/contact" element={
          <Page><Contact /></Page>
        } />
        <Route path="/frameworks" element={
          <Page><Frameworks /></Page>
        } />
        <Route path="/verify" element={
          <Page><Verify /></Page>
        } />
        <Route path="/join" element={
          <Page><Join /></Page>
        } />
        <Route path="/login" element={
          <Page><Login /></Page>
        } />
        <Route path="/signup" element={
          <Page><Login /></Page>
        } />

        {/* ── PROTECTED PAGES ──────────────────────────────────────── */}
        <Route path="/dashboard"  element={<ProtectedPage><Dashboard  /></ProtectedPage>} />
        <Route path="/assessment" element={<ProtectedPage><Assessment /></ProtectedPage>} />
        <Route path="/vendors"    element={<ProtectedPage><Vendors    /></ProtectedPage>} />
        <Route path="/compliance" element={<ProtectedPage><Compliance /></ProtectedPage>} />
        <Route path="/insurance"  element={<ProtectedPage><Insurance  /></ProtectedPage>} />
        <Route path="/reports"    element={<ProtectedPage><Reports    /></ProtectedPage>} />
        <Route path="/settings"   element={<ProtectedPage><Settings   /></ProtectedPage>} />

        {/* ── FALLBACK ─────────────────────────────────────────────── */}
        <Route path="*" element={<Page><NotFound /></Page>} />

      </Routes>
    </AnimatePresence>
  );
}

// ─── App root ─────────────────────────────────────────────────────────────────
export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <Router>
        <div style={{ backgroundColor: "#0f172a" }}>
          <AnimatedRoutes />
          <CookieBanner />
          <SessionTimeout />
          <TrialExpiredModal />
        </div>
      </Router>
    </QueryClientProvider>
  );
}